import * as React from 'react'

// Mirrors KitRegistration::INSTRUMENTS
const INSTRUMENTS = [
    'Sciex Triple Quad 5500/6500',
    'Thermo Scientific Q Exactive Orbitrap',
    'Thermo TSQ Altis',
    'Agilent 6495D LC/TQ',
    'Waters',
    'Sciex Triple Quad 7500',
]

const SUPPORT_UPLC_INSTRUMENTS = ['Sciex Triple Quad 5500/6500', 'Agilent 6495D LC/TQ']

// Mirrors SystemPerformanceAssessment::PANEL_OPTIONS
const PANEL_OPTIONS = [
    { value: 'Panel_A', label: 'Panel A' },
    { value: 'Panel_B', label: 'Panel B' },
    { value: 'DFI', label: 'DFI' },
]

const SPA_RULE_OPTIONS = ['HPLC', 'UPLC']

function getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content ?? ''
}

export default SystemPerformanceAssessmentForm = () => {
    const [name, setName] = React.useState('')
    const [instrument, setInstrument] = React.useState(INSTRUMENTS[0])
    const [panel, setPanel] = React.useState(PANEL_OPTIONS[0].value)
    const [spaRule, setSpaRule] = React.useState(SPA_RULE_OPTIONS[0])
    const [rtMin, setRtMin] = React.useState(0.0)
    const [rtMax, setRtMax] = React.useState(0.0)
    const [file, setFile] = React.useState(null)
    const [isDragOver, setIsDragOver] = React.useState(false)
    const [submitting, setSubmitting] = React.useState(false)

    const [csrfToken] = React.useState(getCsrfToken)
    const fileInputRef = React.useRef(null)

    // DFI runs are direct-infusion — column type is not applicable, only the RT window is.
    // Panel A/B runs are LC-based — column type matters, RT window is not applicable.
    const isDfi = panel === 'DFI'
    const hasUplc = SUPPORT_UPLC_INSTRUMENTS.includes(instrument)

    // Keep the column-type selection consistent with what the chosen instrument supports.
    React.useEffect(() => {
        if (isDfi) return
        setSpaRule((prev) => (hasUplc ? (prev || SPA_RULE_OPTIONS[0]) : 'HPLC'))
    }, [instrument, isDfi, hasUplc])

    const namingFileLabel = isDfi ? 'DFI' : spaRule

    function assignFileToInput(selected) {
        if (fileInputRef.current) {
            const dataTransfer = new DataTransfer()
            dataTransfer.items.add(selected)
            fileInputRef.current.files = dataTransfer.files
        }
        setFile(selected)
    }

    function handleDownloadNamingFile(e) {
        e.preventDefault()

        const params = new URLSearchParams({
            instrument,
            panel,
            name,
            spa_rule: isDfi ? '' : spaRule,
        })

        fetch(`/system_performance_assessments/download_naming_file?${params.toString()}`, {
            method: 'GET',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
            .then((response) => {
                if (!response.ok) throw new Error('Network response was not ok')
                const contentDisposition = response.headers.get('Content-Disposition')
                let fileName = 'RT_Naming_test.zip'
                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename="(.+)"/)
                    if (fileNameMatch && fileNameMatch[1]) fileName = fileNameMatch[1]
                }
                return response.blob().then((blob) => ({ blob, fileName }))
            })
            .then(({ blob, fileName }) => {
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = fileName
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            })
            .catch((error) => console.error('Error during file download:', error))
    }

    function handleSubmit(e) {
        if (!file) {
            e.preventDefault()
            alert('Please attach a .zip data file before submitting.')
            return
        }
        setSubmitting(true)
        // No preventDefault — the browser performs a normal multipart POST and
        // follows the controller's redirect, which is how flash messages are shown.
    }

    return (
        <form
            action="/system_performance_assessment/perform"
            method="post"
            encType="multipart/form-data"
            data-turbo="false"
            onSubmit={handleSubmit}
        >
            <input type="hidden" name="authenticity_token" value={csrfToken} />

            <div className="kit-reg-card">
                {/* STEP 1: Assessment Information */}
                <div className="kit-reg-section">
                    <h6 className="kit-reg-section-title">
                        <i className="bi bi-1-circle me-2"></i>
                        Assessment Information
                    </h6>

                    <div className="kit-reg-grid">
                        <div className="kit-reg-field">
                            <label className="kit-reg-label" htmlFor="spa-name">Assessment Name</label>
                            <input
                                type="text"
                                id="spa-name"
                                name="name"
                                className="kit-reg-input"
                                placeholder="Enter name"
                                maxLength={30}
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>

                        <div className="kit-reg-field">
                            <label className="kit-reg-label" htmlFor="instrument-select">Instrument</label>
                            <select
                                id="instrument-select"
                                name="instrument"
                                className="kit-reg-select"
                                required
                                value={instrument}
                                onChange={(e) => setInstrument(e.target.value)}
                            >
                                {INSTRUMENTS.map((inst) => (
                                    <option value={inst} key={inst}>{inst}</option>
                                ))}
                            </select>
                        </div>

                        <div className="kit-reg-field">
                            <label className="kit-reg-label" htmlFor="panel-select-spa">Panel</label>
                            <select
                                id="panel-select-spa"
                                name="panel"
                                className="kit-reg-select"
                                required
                                value={panel}
                                onChange={(e) => setPanel(e.target.value)}
                            >
                                {PANEL_OPTIONS.map((opt) => (
                                    <option value={opt.value} key={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        {!isDfi && (
                            <div className="kit-reg-field">
                                <label className="kit-reg-label" htmlFor="hplc-uplc-select">Column</label>
                                <select
                                    id="hplc-uplc-select"
                                    name="spa_rule"
                                    className="kit-reg-select"
                                    required
                                    value={spaRule}
                                    onChange={(e) => setSpaRule(e.target.value)}
                                >
                                    {SPA_RULE_OPTIONS.map((rule) => (
                                        <option value={rule} key={rule} disabled={rule === 'UPLC' && !hasUplc}>{rule}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {isDfi && (
                            <div className="d-flex justify-content-between gap-2" id="rt-range-inputs-spa">
                                <div className="kit-reg-field">
                                    <label className="kit-reg-label">Min RT</label>
                                    <input
                                        type="number"
                                        id="rtmin-input"
                                        name="rtmin"
                                        className="kit-reg-input"
                                        step={0.1}
                                        min={0.0}
                                        required
                                        value={rtMin}
                                        onChange={(e) => setRtMin(e.target.value)}
                                    />
                                </div>
                                <div className="kit-reg-field">
                                    <label className="kit-reg-label">Max RT</label>
                                    <input
                                        type="number"
                                        id="rtmax-input"
                                        name="rtmax"
                                        className="kit-reg-input"
                                        step={0.1}
                                        min={0.0}
                                        required
                                        value={rtMax}
                                        onChange={(e) => setRtMax(e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="kit-reg-field mt-2">
                            <button
                                type="button"
                                id="download-naming-file-spa"
                                className="btn-lc-big-faded w-100"
                                onClick={handleDownloadNamingFile}
                            >
                                <i className="bi bi-download me-1"></i>
                                Download {namingFileLabel} Acquisition Files
                            </button>
                        </div>
                    </div>
                </div>

                {/* STEP 2: Upload Data & Submit */}
                <div className="kit-reg-section mb-0 border-0">
                    <h6 className="kit-reg-section-title">
                        <i className="bi bi-2-circle me-2"></i>
                        Upload Unscheduled Data
                    </h6>
                    <p className="text-muted mb-3" style={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                        Run your instrument using the downloaded acquisition files, then upload the generated .zip data file below.
                    </p>

                    <div className="kit-reg-grid">
                        <div className="kit-reg-field">
                            <label className="kit-reg-label">Data Upload (.zip)</label>
                            <div
                                className={`custom-file-drop ${isDragOver ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
                                style={{ minHeight: 80, position: 'relative', borderRadius: 6 }}
                                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true) }}
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true) }}
                                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false) }}
                                onDrop={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setIsDragOver(false)
                                    const dropped = e.dataTransfer.files
                                    if (dropped.length > 0) assignFileToInput(dropped[0])
                                }}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    name="unscheduled_data"
                                    accept=".zip"
                                    required
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 2 }}
                                    onChange={(e) => {
                                        if (e.target.files.length > 0) setFile(e.target.files[0])
                                    }}
                                />
                                <span className="drop-text" style={{ pointerEvents: 'none', display: 'flex', alignItems: 'center', zIndex: 1 }}>
                                    <i className={`bi ${file ? 'bi-file-earmark-check' : 'bi-cloud-arrow-up'} me-2`}></i>
                                    <span>{file ? file.name : 'Drag & Drop .zip File'}</span>
                                </span>
                            </div>
                        </div>

                        <div className="kit-reg-field mt-2">
                            <button
                                type="submit"
                                id="submit-unscheduled-data"
                                className="btn-lc-big w-100"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                data-bs-title="Run Assessment"
                                data-bs-trigger="hover"
                                disabled={submitting}
                            >
                                <i className="bi bi-cpu me-1"></i>
                                {submitting ? 'Submitting\u2026' : 'Perform Assessment'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    )
}
